/**
 * The overlay every dialog in the client was repeating, plus the behaviour.
 *
 * Eight shells wrote the same `position: fixed; inset: 0; display: flex;
 * align-items: center; justify-content: center` with a scrim and a z-index, and
 * five of them wired Escape by hand on `window` or `document` -- which is why
 * pressing Escape with two open closed both.
 *
 * Deliberately not a look. It gives you a dismissible layer and the child draws
 * its own box, because a celebration and a panel are different things that
 * happen to float over the screen. Vesper's panel is Dialog, built on this; a
 * celebration keeps drawing its own.
 *
 *     <Modal open={open} onDismiss={close} label={t('...')}>
 *       <div className={styles.myCard}>…</div>
 *     </Modal>
 *
 * It is rendered above the page (`abovePage`, page-layers.tsx) and, while any
 * modal is open, the app's root is `inert`: nothing behind it takes a click, a
 * Tab or a screen reader's focus, and the one-gold rule counts only what is on
 * the dialog. So a layer that must stay live over a dialog — a toast, a banner
 * above the dialogs — never renders inside the root: it goes above the page
 * too, as a popover, a hover card or a tip does from a control in a dialog.
 * A FloatingWindow renders into the body as well but belongs to the page
 * (`withPage`), and goes inert with it — as does what hangs from a control on
 * the page (useHang).
 *
 * That inertness is what makes it modal, and it says so to nothing else: it
 * sets no aria-modal. Where the platform maps aria-modal="true" — Apple's
 * accessibility API, which VoiceOver reads in Safari and in Chrome alike, and
 * Android's — everything outside the dialog holding the focus is pruned from
 * the accessibility tree, the layers kept live above the page with it: a
 * toast, the page top's "Reconnecting…", a verdict said beside the quiz's
 * dialog would go unheard while a dialog is open (Core-AAM's mapping of
 * aria-modal for the AX API; WebKit's ignoredFromModalPresence; Chromium's
 * pruning, on for Mac and Android). The page and every modal under the top
 * one are inert instead, which hides them from assistive technology as the
 * HTML standard asks of inert, and leaves what stands above the page heard.
 * design:check holds it (aria-modal-true). Open, it carries `data-modal`, so a
 * script can find the dialog on top.
 * For Vesper's own panel on top of this, see Dialog; a confirmation is
 * ConfirmDialog; a notice over the page is NoticeDialog; a panel from the
 * screen's edge is Drawer.
 *
 * `layer` is the rung it is drawn on, a name its sheet maps to the z-ladder:
 *
 *   dialog   --z-overlay, under every moment: a dialog, a drawer, a question
 *   moment   --z-moment, over every dialog: a mission done, the year-end,
 *            the advisor — and what the server opens over play, the HQ vote and
 *            the quiz (game/common/moment/ is the moments' shell)
 *   notice   --z-notification, over every dialog and moment: NoticeDialog
 *
 * Closed, it takes effect at once — the page live again, the focus back on
 * what opened it, its place among the layers given up — and leaves in its
 * own time: inert, no longer a dialog, still showing what it showed last
 * while open, its scrim and what is on it fading in a panel's time — a
 * drawer's in the dock's, sliding back to its edge — and gone as the fade
 * ends; at once under reduced motion. One whose owner takes it away rather
 * than closing it — a moment, as its store moves on — goes at once.
 *
 * Two can be open at once — a moment arrives while another is up — and the one
 * drawn on top is the one that takes the press: the later on the same rung,
 * or the one on the higher rung. Each registers its place as it opens
 * (modal-layers.ts), which makes the one it covers inert, as the page is —
 * no press, no key, no screen reader, and its gold not counted — until it is
 * on top again; and what renders inside a modal asks `useCovered()` whether
 * another is drawn over it, so a clock it keeps holds while it cannot be
 * seen. What hangs from a control inside any modal — a popover, a hover
 * card — is inert with it while another is drawn over it or it leaves, and
 * inside a moment or a notice hangs on its rung, after it, not under it;
 * what hangs from a control on the page is inert with the page while any
 * modal is open, and drawn on the page's rung, under the dialog's scrim
 * (`useHang`). An inert layer takes neither the keys nor the focus however
 * high it is drawn (use-modal-behavior.ts's `reachableOnTop`): every caller of
 * the behaviour hook puts its ref on the layer it closes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { ModalLayer, openLayer } from './modal-layers';
import { abovePage } from './page-layers';
import { rungOf, useModalBehavior } from './use-modal-behavior';
import { useWayOut } from '../../utils/use-way-out';
import styles from './modal.module.css';

const PLACEMENT = {
  center: '',
  start: styles.start,
  end: styles.end,
} as const;

/** The rung each layer is drawn on (modal.module.css), and what hangs from a control inside it hangs over. */
const LAYER = {
  dialog: { className: '', rung: null },
  moment: { className: styles.moment, rung: 'moment' },
  notice: { className: styles.notice, rung: 'notice' },
} as const;

interface ModalProps {
  open: boolean;
  /** Escape, and a click on the scrim if `dismissOnScrimClick`. */
  onDismiss?: () => void;
  children: ReactNode;
  /** Accessible name. A dialog with no name is announced as just "dialog". */
  label?: string;
  /** The id of the element that titles it, when the title is already on screen. */
  labelledBy?: string;
  /** The id of the element that says what it is for. */
  describedBy?: string;
  /** `alertdialog` for a question that must be answered — a confirmation. */
  role?: 'dialog' | 'alertdialog';
  /** Where its child sits: centred, or against the start or end edge (a drawer). */
  placement?: keyof typeof PLACEMENT;
  /**
   * With placement start or end: the layer, its scrim with it, begins this far
   * from that edge — what is beside it stays lit, inert as the page is: the
   * rail, beside the menu's drawer.
   */
  offset?: string;
  dismissOnScrimClick?: boolean;
  /** The rung it is drawn on: a dialog's (the default), a moment's over every dialog, or a notice's over every moment. */
  layer?: keyof typeof LAYER;
}

export function Modal({
  open,
  onDismiss,
  children,
  label,
  labelledBy,
  describedBy,
  role = 'dialog',
  placement = 'center',
  offset,
  dismissOnScrimClick = false,
  layer = 'dialog',
}: ModalProps) {
  const [id] = useState(() => Symbol('modal'));
  const overlay = useRef<HTMLDivElement>(null);
  // Closed, it leaves in its own time (its sheet's [data-leaving]), drawing
  // what it showed last while open — a caller may take its words away as it
  // closes — and is gone once its fade has run; at once under reduced motion.
  const way = useWayOut(open, children);
  const { leaving } = way;
  // What renders inside it learns it is leaving as it learns it is covered:
  // a clock it keeps holds, and nothing on it goes by itself while it goes.
  const place = useMemo(() => ({ id, leaving }), [id, leaving]);
  // While it is open the page behind is inert, and so is every modal drawn
  // under it: its place among them is its rung, read off the overlay the
  // portal has mounted, and its turn in the order they opened. Before the
  // behaviour hook, on purpose: effects clean up in the order they were
  // declared, so as it closes the page, and the modal it leaves on top, are
  // live again before the hook hands the focus back into them — an inert
  // element cannot take focus; and as it opens, what it covers turns inert
  // with the focus still in it for the hook to take — the browser moves a
  // focus an inert element holds only at its next frame.
  useEffect(() => {
    if (!open) return;
    const element = overlay.current;
    return openLayer(id, element ? rungOf(element) || 0 : 0, element);
  }, [open, id]);
  // The page behind does not scroll, and focus stays inside: every dialog.
  const behaviourRef = useModalBehavior<HTMLDivElement>({ isOpen: open, onDismiss });
  // The overlay, for the layers and for the behaviour hook alike.
  const placeOverlay = useCallback(
    (element: HTMLDivElement | null) => {
      overlay.current = element;
      behaviourRef.current = element;
    },
    [behaviourRef],
  );

  if (!open && !leaving) return null;

  // How far from its edge it begins: the caller's, at runtime; the
  // placement's rule reads it.
  const offsetStyle = offset ? ({ '--modal-offset': offset } as CSSProperties) : undefined;

  // Leaving, it is no longer a dialog: a picture of one going, inert — no
  // press, no key, no screen reader, no gold counted — and no longer marked a
  // modal (data-modal) to a script that looks for the dialog on top. The page
  // behind is live already and the focus back where it was: closing took
  // effect at once.
  return abovePage(
    <div
      // Each opening its own overlay, appended to the body as it opens: one
      // opened again while it is still leaving is drawn afresh, on top in the
      // order the layers and the keys go by, rather than revived where it was.
      key={way.appearances}
      ref={placeOverlay}
      role={leaving ? undefined : role}
      data-modal={leaving ? undefined : ''}
      aria-label={leaving || labelledBy ? undefined : label}
      aria-labelledby={leaving ? undefined : labelledBy}
      aria-describedby={leaving ? undefined : describedBy}
      inert={leaving}
      data-leaving={leaving ? '' : undefined}
      className={[styles.overlay, PLACEMENT[placement], LAYER[layer].className].filter(Boolean).join(' ')}
      style={offsetStyle}
      onClick={
        dismissOnScrimClick
          ? (e) => {
              // Only a click that landed on the overlay itself, never one that
              // bubbled up from inside the dialog.
              if (e.target === e.currentTarget) onDismiss?.();
            }
          : undefined
      }
      // Its own fade's end, not a drawer's slide inside it.
      onAnimationEnd={leaving ? way.left : undefined}
    >
      <ModalLayer.Provider value={place}>{way.shown}</ModalLayer.Provider>
    </div>,
    // On its layer's rung, which what hangs from a control in it hangs on; a dialog's own place is its ModalLayer.
    LAYER[layer].rung,
  );
}
